import service from "@irati-tracking/tracker"
import localStorage from "@irati/region/helpers/storage"

export enum LoginType {
  otpLogin = "otp_login",
  phoneOtpLogin = "phone_otp_login",
  passwordLogin = "password_login",
  ssoLogin = "sso_login",
  activationEmail = "activation_email",
  adminActivationInvite = "admin_activation_invite",
  phoneCapture = "phone_capture"
}

export enum LoginProduct {
  login = "login",
  activation = "activation",
  myAccount = "my_account"
}

export enum LoginAvailable {
  password = "password",
  sso = "sso",
  passwordAndSSO = "password_and_sso"
}

interface SharingData {
  source: string
  type: string
}

export const getLoginDetails = (detail?: string, type?: LoginType, product?: LoginProduct, email?: string, phone?: string) => {
  return {
    detail: detail || getLocalStorageItem("login_detail"),
    type: type || getLocalStorageItem("login_type"),
    product: product || getLocalStorageItem("login_product"),
    login_available: getLocalStorageItem("login_available"),
    domain: !!getLocalStorageItem("login_domain"),
    domain_name: getLocalStorageItem("login_domain"),
    email: email,
    phone: phone
  }
}

export const getLocalStorageItem = (value: string) => {
  return !localStorage.getItem(value) ? undefined : localStorage.getItem(value)
}

const tracking = {
  view: (page: string, data?: object) => {
    service.trackPlatformEvent(`platform_view_${page}`, data)
  },
  click: (item: string, data: object) => {
    service.trackPlatformEvent(`platform_click_${item}`, data)
  },
  dismiss: (type: string, source: string, data: object) => {
    service.trackPlatformEvent("platform_dismiss", {...data, type, source})
  },
  openLiveChat: () => {
    service.trackPlatformEvent("platform_opened_live_chat", {})
  },
  clickHeaderHomeButton: (data: object) => {
    service.trackPlatformEvent("platform_click_header_home", data)
  },
  clickHeaderMyAccount: (data: object) => {
    service.trackPlatformEvent("platform_click_header_myaccount", data)
  },
  notificationOptInSkipped: () => {
    service.trackPlatformEvent("platform_notifications_optin_skipped", {})
  },
  notificationOptInStarted: () => {
    service.trackPlatformEvent("platform_notifications_optin_started", {})
  },
  notificationOptInCompleted: (status: string) => {
    service.trackPlatformEvent("platform_notifications_optin_completed", {status})
  },
  notificationOptInSeen: () => {
    service.trackPlatformEvent("platform_notifications_optin_seen", {})
  },
  loadingSeen: () => {
    service.trackPlatformEvent("platform_loading_seen", {})
  },
  loadingCompleted: () => {
    service.trackPlatformEvent("platform_loading_completed", {})
  },
  onBoarding: (step: string) => {
    service.trackPlatformEvent("onboarding_view_step", {step})
  },
  onBoardingComplete: () => {
    service.trackPlatformEvent("onboarding_complete", {})
  },
  navigationClick: (data: object) => {
    service.trackPlatformEvent("navigation_button_click", data)
  },
  updateAppPrompt(type: string) {
    const data = {
      type,
      source: "force_update"
    }
    service.trackPlatformEvent("platform_update_app_prompt", data)
  },
  enterPassword: () => {
    service.trackEvent("enter_password")
  },
  successCreatePassword: () => {
    service.trackEvent("success_create_password")
  },
  share: (data: SharingData) => {
    service.trackEvent("share", data)
  },
  pageViewed: () => {
    service.trackEvent("page_viewed")
  },
  loginViewPage: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("pageview", getLoginDetails(detail, type, product))
  },
  loginOTPSubmit: (detail: string, type?: LoginType, product?: LoginProduct, email?: string, phone?: string, available?: string) => {
    service.trackEvent("submit", getLoginDetails(detail, type, product, email, phone, available))
  },
  loginOTPSuccess: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("success", getLoginDetails(detail, type, product))
  },
  loginOTPError: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("error", getLoginDetails(detail, type, product))
  },
  loginOTPResend: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("resend", getLoginDetails(detail, type, product))
  },
  loginErrorDetails: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("error_details", getLoginDetails(detail, type, product))
  },
  loginCTAClick: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("cta_click", getLoginDetails(detail, type, product))
  },
  loginActivation: (detail: string, product?: LoginProduct) => {
    service.trackEvent("complete_activation", getLoginDetails(detail, undefined, product))
  },
  loginSuccess: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("login_success", getLoginDetails(detail, type, product))
  },
  phoneCaptureViewPage: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("phone_number_capture_page", getLoginDetails(detail, type, product))
  },
  phoneCaptureSuccess: (detail: string, type?: LoginType, product?: LoginProduct) => {
    service.trackEvent("phone_verification_success", getLoginDetails(detail, type, product))
  },
  phoneCaptureSkip: (detail: string, type?: LoginType) => {
    service.trackEvent("skip", getLoginDetails(detail, type))
  }
}

export default tracking
